import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  NextPage,
} from "next";
import { TemplateText, Text } from "src/presentation/components/atoms";
import { MainLayout } from "src/presentation/layouts";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useEffect, useState } from "react";
import { NoticiasService } from "@services/noticias-service";
import { settings } from "@config/settings";
import { localesParserDictionary } from "@utils/locales-parser";
import NoticiasIcon from "@assets/icons/medios_noticias.svg";
import CatalogoIcon from "@assets/icons/medios_catalogo.svg";
import LogosIcon from "@assets/icons/medios_logos.svg";
import MultimediaIcon from "@assets/icons/medios_multimedia.svg";
import MediosIcon from "@assets/icons/medios_medios.svg";
import styles from "./medios-page.module.scss";
import { useTranslation } from "next-i18next";
import cx from "classnames";
import { Noticia } from "@models/noticia";
import {
  ManvertMediosGrid,
  NoticiasGrid,
  MultimediaGrid,
  CatalogosGrid,
  LogosGrid,
} from "src/presentation/components/organisms";
import { ManvertMedio } from "@models/manvert-medio";
import { ManvertMedioService } from "@services/manvert-medio-service";
import { useRouter } from "next/router";
import { MultimediaService } from "@services/multimedia";
import { Multimedia } from "@models/multimedia";
import { Catalogo } from "@models/catalogo";
import { CatalogoService } from "@services/catalogo-service";
import { Logo } from "@models/logo";
import { LogosService } from "@services/logos-service";
import { NextSeo } from "next-seo";
import { MainRouter } from "@config/urls";
import { CountryLocationService } from "@services/country-location-service";
import { EntitiesFetcherService } from "@services/entities-fetcher";

const MediosPage: NextPage = (props: any) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { noticiasData } = props;

  const [catSelected, setCatSelected] = useState("noticias");
  // Datas
  const [noticias, setNoticias] = useState<Array<Noticia>>(
    JSON.parse(noticiasData)
  );
  const [medios, setMedios] = useState<Array<ManvertMedio>>([]);
  const [multimedia, setMultimedia] = useState<Array<Multimedia>>([]);
  const [catalogos, setCatalogos] = useState<Array<Catalogo>>([]);
  const [logos, setLogos] = useState<Array<Logo>>([]);

  // Selected grid
  const [gridSelected, setGridSelected] = useState<any>(
    <NoticiasGrid noticiasData={JSON.parse(noticiasData)} />
  );

  useEffect(() => {
    new LogosService().getLogos().then((response) => {
      setLogos(response);
    });
  }, []);

  useEffect(() => {
    new CatalogoService().getCatalogos().then((response) => {
      setCatalogos(response);
    });
  }, []);

  useEffect(() => {
    new ManvertMedioService().getManvertMedios().then((response) => {
      setMedios(response);
    });
  }, [router.locale]);

  useEffect(() => {
    new MultimediaService().getMultimedia().then((response) => {
      setMultimedia(response);
    });
  }, [router.locale]);

  useEffect(() => {
    setGridSelected(<NoticiasGrid noticiasData={noticias} />);
  }, [noticiasData, noticias]);

  const categories = [
    {
      key: "noticias",
      icon: <NoticiasIcon />,
      grid: <NoticiasGrid noticiasData={noticias} />,
    },
    {
      key: "medios",
      icon: <MediosIcon />,
      grid: <ManvertMediosGrid mediosData={medios} />,
    },
    {
      key: "multimedia",
      icon: <MultimediaIcon />,
      grid: <MultimediaGrid multimediaData={multimedia} />,
    },
    {
      key: "catalogo",
      icon: <CatalogoIcon />,
      grid: <CatalogosGrid catalogosData={catalogos} />,
    },
    {
      key: "logos",
      icon: <LogosIcon />,
      grid: <LogosGrid logosData={logos} />,
    },
  ];

  const handleCategoryChange = (value: any, grid: any) => {
    setCatSelected(value);
    setGridSelected(grid);
  };
  return (
    <>
      <NextSeo
        title={t("SEOMeta.MediosView.title") || ""}
        description={t("SEOMeta.MediosView.description") || ""}
      />
      <MainLayout
        breadcrumb={[
          {
            label: t("urls.home"),
            url: MainRouter.home.generatePath(),
          },
          {
            label: t("urls.nosotros"),
            url: MainRouter.medios.generatePath(),
          },
        ]}
      >
        <div className={styles.root}>
          <Text as="h1" className={styles.mainTitle}>
            {t("MediosView.title")}
          </Text>
          <div className={styles.categoryPicker}>
            {categories.map((item) => {
              return (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => handleCategoryChange(item.key, item.grid)}
                  className={cx(styles.category, {
                    [styles.selected]: item.key === catSelected,
                  })}
                >
                  <TemplateText template="normal">
                    {t("MediosView." + item.key)}
                  </TemplateText>

                  {item.icon}
                </button>
              );
            })}
          </div>
          <div className={styles.categoryDataContainer}>{gridSelected}</div>
        </div>
      </MainLayout>
    </>
  );
};

export async function getServerSideProps(
  Props: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<any>> {
  const { locale, req, res } = Props;
  let selectedCountry =
    await new CountryLocationService().getSelectedCountryData(req, res);

  const noticias = await new NoticiasService().getNoticias(
    localesParserDictionary[locale || settings.DEFAULT_LANG]
  );

  const opts = {
    locale: localesParserDictionary[locale || settings.DEFAULT_LANG],
    country: selectedCountry.countrycode,
  };

  const entities = await new EntitiesFetcherService().getEntities({
    ...opts,
    productosDestacados: true,
    productosGenericos: true,
  });

  return {
    props: {
      country: selectedCountry.countrycode,
      noticiasData: JSON.stringify(noticias),
      productosGenericos: JSON.stringify(entities.productosGenericosData),
      productosDestacadosData: JSON.stringify(entities.productosDestacadosData),
      ...(await serverSideTranslations(locale || settings.I18N_FALLBACK, [
        "common",
      ])),
    },
  };
}

export default MediosPage;
